/**
 * The revision timeline: the printed record of how a prototype got here.
 * Selecting a revision shows it in the review above.
 */
import type { PrototypeRevision } from "@yagni/shared";
import { formatRelative } from "../lib/relativeTime.js";

export function RevisionTimeline({
  revisions,
  activeSeq,
  onSelect,
}: {
  revisions: readonly PrototypeRevision[];
  activeSeq: number | null;
  onSelect: (seq: number) => void;
}) {
  return (
    <section className="material-print px-4 py-3" data-testid="prototype-revisions">
      <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-[var(--text-label)] mb-2">
        Revisions
      </div>
      {revisions.length === 0 ? (
        <p className="text-[12.5px] text-[var(--text-tertiary)] m-0">Revision 1 is the whole record so far.</p>
      ) : (
        <ul className="list-none p-0 m-0 flex flex-col">
          {revisions.map((rev) => {
            const selected = rev.seq === activeSeq;
            return (
              <li key={rev.id}>
                <button
                  type="button"
                  onClick={() => onSelect(rev.seq)}
                  aria-pressed={selected}
                  className={`w-full flex items-baseline gap-2.5 text-left px-2 py-1.5 rounded-[var(--radius-control)] transition-colors ${
                    selected ? "bg-[var(--accent-muted)]" : "hover:bg-[var(--bg-message-hover)]"
                  }`}
                >
                  <span
                    className={`font-mono text-[11px] font-semibold flex-none ${
                      selected ? "text-[var(--accent)]" : "text-[var(--text-tertiary)]"
                    }`}
                  >
                    R{rev.seq}
                  </span>
                  <span className="text-[12.5px] text-[var(--text-primary)] flex-1 min-w-0 truncate">
                    {rev.note?.trim() || `Revision ${rev.seq}`}
                  </span>
                  <span className="font-mono text-[10px] text-[var(--text-quiet)] flex-none">
                    {formatRelative(rev.createdAt)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
